import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../../../../store/root-reducer';
import {
  CallLog,
  DetailedCallLog,
  GetCallLogsListPayload,
  CallDirection,
} from '../../../types/call-logs';

interface UseCallLogsParams {
  tabType: GetCallLogsListPayload['type'];
}

interface UseCallLogsReturn {
  callLogs: CallLog[];
  total: number;
}

// same mapDetailedToSimpleCallLog and RESPONSE_KEYS_BY_TAB as before…

export const mapDetailedToSimpleCallLog = (log: DetailedCallLog): CallLog => {
  let callDirection: CallDirection;
  switch (log.callType) {
    case 'incoming':
      callDirection = CallDirection.Incoming;
      break;
    case 'missed':
    case 'missed-with-voicemail':
      callDirection = CallDirection.Missed;
      break;
    case 'outgoing':
      callDirection = CallDirection.Outgoing;
      break;
    default:
      callDirection = CallDirection.Missed;
      break;
  }

  const prospectFirstName = log.prospect?.firstName ?? '';
  const prospectLastName = log.prospect?.lastName ?? '';
  const prospectName = `${prospectFirstName} ${prospectLastName}`.trim();

  const callerEntity =
    log?.user || log?.attendantUser || log?.recipientUser || log?.prospect;
  const callerFirstName = callerEntity?.firstName ?? '';
  const callerLastName = callerEntity?.lastName ?? '';
  const callerHost = `${callerFirstName} ${callerLastName}`.trim();

  const sequenceTitle = log.sequence?.title ?? '';
  const hasVoicemail = Boolean(log.isRecordingPresent);
  const transcription = log.transcription ?? null;

  return {
    id: log?.id,
    callId: String(log?.id),
    prospectName: prospectName || log?.phoneNumber?.number || '',
    prospectPhone: log?.phoneNumber || {},
    callDirection,
    recordingDuration: log?.recordingDuration,
    callDuration: log?.callDuration,
    ringDuration: log?.ringTime,
    callOutcome: log?.callOutcome ?? null,
    dateTime: log?.callDateTime,
    callerHost,
    source: log?.source,
    recordingUrl: null,
    attendantUser: log?.attendantUser,
    recipientUser: log?.recipientUser,
    forwardedNumber: log?.forwardedNumber,
    sequenceTitle,
    hasVoicemail,
    transcription,
    isDirectCall: log?.isDirectCall,
    isForwarded: log?.isForwarded,
    isRecordingPresent: log?.isRecordingPresent,
    isSeen: log?.isSeen,
    isTranscriptionInProgress: log?.isTranscriptionInProgress,
    taskId: log?.taskId,
    prospect: log?.prospect,
    isProspectAccessible: log?.isProspectAccessible,
  };
};

const RESPONSE_KEYS_BY_TAB: Record<GetCallLogsListPayload['type'], string> = {
  all: 'allCalls',
  missed: 'missedCalls',
  voicemails: 'incomingVoicemails',
};

/** Maps a dialer list API `payload` (same shape as Redux list responses) to table rows. */
export const mapDialerListPayloadToCallLogs = (
  sourceData: unknown,
  tabType: GetCallLogsListPayload['type'],
): { callLogs: CallLog[]; total: number } => {
  if (!sourceData) {
    return { callLogs: [], total: 0 };
  }

  const data: any = sourceData;

  if (Array.isArray(data.callLogs)) {
    return {
      callLogs: data.callLogs as CallLog[],
      total: data.total ?? data.callLogs.length ?? 0,
    };
  }

  const responseKey = RESPONSE_KEYS_BY_TAB[tabType] ?? 'allCalls';
  const rawLogs = data[responseKey];

  if (Array.isArray(rawLogs)) {
    const detailedLogs = rawLogs as DetailedCallLog[];
    return {
      callLogs: detailedLogs.map(mapDetailedToSimpleCallLog),
      total: data.total ?? detailedLogs.length ?? 0,
    };
  }

  return { callLogs: [], total: 0 };
};

export const useCallLogs = ({
  tabType,
}: UseCallLogsParams): UseCallLogsReturn => {
  const activeCallLogsResponse = useSelector(
    (state: RootState) => state.dialer.activeCallLogsResponse,
  );

  const sourceData = activeCallLogsResponse;
  return useMemo(() => {
    if (!sourceData) {
      return { callLogs: [], total: 0 };
    }

    const data: any = sourceData;

    // Flat shape: { callLogs: [...], total }
    if (Array.isArray(data.callLogs)) {
      return {
        callLogs: data.callLogs as CallLog[],
        total: data.total ?? data.callLogs.length ?? 0,
      };
    }

    // Grouped shape: { allCalls/missedCalls/incomingVoicemails: [...], total }
    const responseKey = RESPONSE_KEYS_BY_TAB[tabType] ?? 'allCalls';
    const rawLogs = data[responseKey];

    if (Array.isArray(rawLogs)) {
      const detailedLogs = rawLogs as DetailedCallLog[];
      return {
        callLogs: detailedLogs.map(mapDetailedToSimpleCallLog),
        total: data.total ?? detailedLogs.length ?? 0,
      };
    }

    return { callLogs: [], total: 0 };
  }, [sourceData, tabType]);
};
